import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { X, Check, Sparkles, Brain, Users, Shield, Crown } from 'lucide-react';

const paywallContent = {
    plus: {
        headline: "Turn your emotions into clarity",
        subheadline: "MoodTrack Plus helps you understand why you feel the way you do — not just log it.",
        valueProps: [
            { icon: Brain, text: "Discover emotional patterns across weeks & months" },
            { icon: Sparkles, text: "Get weekly AI reflections that explain your moods" },
            { icon: Users, text: "Stay accountable with people who support you" },
            { icon: Shield, text: "Protect your private thoughts with Face ID" }
        ],
        price: { monthly: 7.99, yearly: 59.99 },
        savings: "37%"
    },
    deeper_insights: {
        headline: "Understand your emotions on a deeper level",
        subheadline: "Deeper Insights turns your journal into meaningful self-awareness.",
        valueProps: [
            { icon: Sparkles, text: "Personalized emotional summaries" },
            { icon: Brain, text: "Trigger & pattern detection" },
            { icon: Check, text: "Monthly mental wellness report" },
            { icon: Check, text: "Burnout & emotional fatigue signals" }
        ],
        price: { monthly: 4.99 },
        isAddon: true
    },
    partners_pro: {
        headline: "Growth is easier with support",
        subheadline: "Upgrade your accountability system to stay consistent and supported.",
        valueProps: [
            { icon: Users, text: "Unlimited accountability partners" },
            { icon: Users, text: "Group accountability circles and chats" },
            { icon: Check, text: "Missed accountability check-ins" },
            { icon: Check, text: "Accountability streaks" }
        ],
        price: { monthly: 2.99 },
        isAddon: true
    }
};

const Paywall = ({ type = 'plus', isOpen, onClose, onUpgrade, feature }) => {
    const content = paywallContent[type];
    
    if (!content || !isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                {/* Gradient Background */}
                <div 
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(135deg, hsl(163 25% 28% / 0.9) 0%, hsl(265 30% 25% / 0.9) 50%, hsl(28 35% 30% / 0.9) 100%)'
                    }}
                />
                
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="relative bg-card rounded-3xl p-8 max-w-md w-full shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors"
                    >
                        <X className="w-5 h-5 text-muted-foreground" />
                    </button>

                    {/* Icon */}
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <Crown className="w-8 h-8 text-primary" />
                    </div>

                    {/* Content */}
                    <div className="text-center mb-6">
                        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                            {content.headline}
                        </h2>
                        <p className="text-muted-foreground">
                            {content.subheadline}
                        </p>
                    </div>

                    {/* Feature that triggered paywall */}
                    {feature && (
                        <div className="bg-accent/20 rounded-xl p-3 mb-6 text-center">
                            <p className="text-sm text-muted-foreground">
                                <span className="font-medium text-foreground">{feature}</span> requires an upgrade
                            </p>
                        </div>
                    )}

                    {/* Value Props */}
                    <ul className="space-y-3 mb-6">
                        {content.valueProps.map((prop, index) => (
                            <motion.li
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="flex items-start gap-3"
                            >
                                <div className="w-6 h-6 bg-chart-1/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <prop.icon className="w-3.5 h-3.5 text-chart-1" />
                                </div>
                                <span className="text-sm">{prop.text}</span>
                            </motion.li>
                        ))}
                    </ul>

                    {/* Pricing */}
                    <div className="text-center mb-6">
                        <div className="flex items-baseline justify-center gap-2">
                            <span className="font-heading text-4xl font-bold">
                                ${content.price.monthly}
                            </span>
                            <span className="text-muted-foreground">/month</span>
                        </div>
                        {content.price.yearly && (
                            <p className="text-sm text-chart-1 mt-1">
                                or ${content.price.yearly}/year — Save {content.savings}
                            </p>
                        )}
                    </div>

                    {/* CTA Button */}
                    <Button
                        onClick={() => onUpgrade(type)}
                        className="w-full rounded-full py-6 text-lg shadow-lg hover:shadow-xl transition-all"
                        data-testid="paywall-upgrade-btn"
                    >
                        <Sparkles className="w-5 h-5 mr-2" />
                        {content.isAddon ? `Add ${content.price.monthly}/mo` : 'Upgrade Now'}
                    </Button>

                    {/* Footer Note */}
                    <p className="text-center text-xs text-muted-foreground mt-4">
                        Cancel anytime. 7-day free trial included.
                    </p>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default Paywall;
